import { useEffect, useMemo, useRef, useState } from "react";
import { GridColDef } from "@mui/x-data-grid";
import {
  ITitleVolume,
  ITitleVolumeGridFilter,
  ITitleVolumeGridFilterSchema,
} from "../api/types";
import {
  Box,
  Button,
  Card,
  CardContent,
  Divider,
  Grid,
  Typography,
} from "@mui/material";
import LoadingButton from "@mui/lab/LoadingButton";
import { useForm } from "react-hook-form";
import { IDropdownModel } from "common/models/IDropdownModel";
import { GetMaterialTypeValues } from "features/materialtype/api/hooks";
import useDashboardStore from "../store/DashboardStore";
import { GetTitleVolume } from "../api/hooks";
import { zodResolver } from "@hookform/resolvers/zod";
import { SelectField } from "components/@extended/SelectField";
import StyledDataGrid from "components/@extended/StyledDataGrid";
import { GetInstituteByLibrary } from "features/institute/api/hooks";
import { GetDepartmentValuesByInstitute } from "features/department/api/hooks";

const TitleVolumeGrid = () => {
  const {
    titleVolumePostModel,
    handleTitleVolumeFiltering,
    handleTitleVolumePagination,
    handleTitleVolumeSorting,
  } = useDashboardStore();

  const { data: getTitleVolumes, isFetching } =
    GetTitleVolume(titleVolumePostModel);

  const columns: GridColDef<ITitleVolume>[] = useMemo(
    () => [
      {
        field: "instituteCode",
        headerName: "Institute Name",
        flex: 1,
      },
      {
        field: "departmentCode",
        headerName: "Department Name",
        flex: 1,
      },
      {
        field: "title",
        headerName: "Materials",
        flex: 1,
        headerAlign: "right",
        align: "right",
      },
      {
        field: "volume",
        headerName: "Accessions",
        flex: 1,
        headerAlign: "right",
        align: "right",
        renderCell: (params) => {
          return (
            <>
              {params.value !== null ? (
                <>{params.value.toLocaleString("en-IN")}</>
              ) : (
                "-"
              )}
            </>
          );
        },
      },
      {
        field: "totalPrice",
        headerName: "Total Price",
        flex: 1,
        headerAlign: "right",
        align: "right",
        renderCell: (params) => {
          return (
            <>
              {params.value !== null ? (
                <>&#x20B9; {params.value.toLocaleString("en-IN")}</>
              ) : (
                "-"
              )}
            </>
          );
        },
      },
    ],
    [],
  );

  const methods = useForm<ITitleVolumeGridFilter>({
    mode: "all",
    resolver: zodResolver(ITitleVolumeGridFilterSchema),
  });

  const { reset, handleSubmit, control, watch } = methods;

  const onSubmit = handleSubmit(async (data) => {
    handleTitleVolumeFiltering(data);
  });

  const { data: materialTypeQuery } = GetMaterialTypeValues();
  const [materialTypeOptions, setMaterialTypeOptions] = useState<
    IDropdownModel[]
  >([]);
  useEffect(() => {
    setMaterialTypeOptions(materialTypeQuery?.apiData || []);
  }, [materialTypeQuery]);

  const { data: instituteQuery } = GetInstituteByLibrary();
  const [instituteOptions, setInstituteOptions] = useState<IDropdownModel[]>(
    [],
  );
  useEffect(() => {
    setInstituteOptions(instituteQuery?.apiData || []);
  }, [instituteQuery]);

  const instituteID = watch("instituteID");
  const { data: departmentQuery } = GetDepartmentValuesByInstitute(instituteID);
  const [departmentOptions, setDepartmentOptions] = useState<IDropdownModel[]>(
    [],
  );
  useEffect(() => {
    setDepartmentOptions(departmentQuery?.apiData || []);
  }, [departmentQuery]);

  function formatNumberWithCommas(num?: number) {
    return num?.toLocaleString("en-IN");
  }

  const handleClear = () => {
    handleTitleVolumeFiltering({
      materialTypeID: null,
    });
  };

  useEffect(() => {
    reset(titleVolumePostModel.filterModel);
  }, [titleVolumePostModel.filterModel, reset]);

  const rowCountRef = useRef(getTitleVolumes?.apiData?.count || 0);
  const rowCount = useMemo(() => {
    if (getTitleVolumes?.apiData?.count !== undefined) {
      rowCountRef.current = getTitleVolumes?.apiData?.count;
    }
    return rowCountRef.current;
  }, [getTitleVolumes?.apiData?.count]);

  return (
    <>
      <Box component="form" onSubmit={onSubmit}>
        <Card elevation={3} sx={{ mb: 2 }}>
          <CardContent>
            <Typography variant="h3" component={"h3"} marginBlockEnd={2}>
              Material & Accessions
              <Divider sx={{ my: 1 }} />
            </Typography>

            <Grid container spacing={2}>
              <Grid item xs={12} md={4} lg={3}>
                <SelectField
                  options={materialTypeOptions}
                  control={control}
                  name="materialTypeID"
                  label="Material Type"
                  placeholder="Select Material Type"
                />
              </Grid>
              <Grid item xs={12} md={4} lg={3}>
                <SelectField
                  options={instituteOptions}
                  control={control}
                  name="instituteID"
                  label="Institute"
                  placeholder="Select Institute"
                />
              </Grid>
              <Grid item xs={12} md={4} lg={3}>
                <SelectField
                  options={departmentOptions}
                  control={control}
                  name="departmentID"
                  label="Department"
                  placeholder="Select Department"
                />
              </Grid>
            </Grid>
            <Grid container spacing={2}>
              <Grid item xs={12} md={4} lg={3} sx={{ mt: 3 }}>
                <LoadingButton variant="contained" sx={{ mr: 1 }} type="submit" loading={isFetching} disabled={isFetching}>Search</LoadingButton>
                <Button
                  variant="contained"
                  color="secondary"
                  onClick={handleClear}
                 disabled={isFetching} >
                  Clear
                </Button>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      </Box>

      <Grid container direction={"row"} spacing={2}>
        <Grid item xs={12} md={12}>
          <Card elevation={3} sx={{ p: 3 }}>
            <Grid
              container
              spacing={5}
              sx={{ p: 2 }}
              alignItems="center"
              justifyContent={"flex-start"}
            >
              <Grid item xs={4}>
                <Typography variant="h5" component={"h5"} marginBlockEnd={2}>
                  Total Materials :{" "}
                  {formatNumberWithCommas(
                    getTitleVolumes?.apiData?.data[0]?.totalTitle,
                  )}
                </Typography>
              </Grid>

              <Grid item xs={4}>
                <Typography variant="h5" component={"h5"} marginBlockEnd={2}>
                  Total Accessions :{" "}
                  {formatNumberWithCommas(
                    getTitleVolumes?.apiData?.data[0]?.totalVolume,
                  )}
                </Typography>
              </Grid>

              <Grid item xs={4}>
                <Typography variant="h5" component={"h5"} marginBlockEnd={2}>
                  Total Price : &#x20B9;{" "}
                  {formatNumberWithCommas(
                    getTitleVolumes?.apiData?.data[0]?.allTotalPrice,
                  )}
                </Typography>
              </Grid>
            </Grid>
            <Grid container sx={{ height: 450 }}>
              <StyledDataGrid
                sx={{
                  "& .MuiDataGrid-columnHeaderTitle": {
                    whiteSpace: "wrap",
                    fontWeight: 600,
                    lineHeight: "normal",
                    fontSize: "13px",
                  },
                  "& .MuiDataGrid-cell": {
                    whiteSpace: "wrap",
                    alignContent: "center",
                    fontSize: 14,
                  },
                }}
                density="compact"
                disableRowSelectionOnClick
                getRowId={(item) => item.uniqueID}
                columns={columns}
                pageSizeOptions={[10, 20, 50, 100]}
                initialState={{
                  pagination: {
                    paginationModel: { ...titleVolumePostModel.pagingModel },
                  },
                }}
                sortModel={titleVolumePostModel.sortModel}
                onSortModelChange={handleTitleVolumeSorting}
                onPaginationModelChange={handleTitleVolumePagination}
                getRowHeight={() => "auto"}
                paginationMode="server"
                sortingMode="server"
                rows={getTitleVolumes?.apiData?.data}
                rowCount={rowCount}
                disableColumnFilter
                disableColumnSorting
                loading={isFetching}
              />
            </Grid>
          </Card>
        </Grid>
      </Grid>
    </>
  );
};

export default TitleVolumeGrid;
